import { pinAMapPosition, MapGeographicPosition, Layer, LayerType, LayerElevationLoadStatus } from '/@/types/map'
import { generatePoint, generateLine, generatePolygon } from '/@/utils/genjson'
import { MapDoodleColor, MapElementEnum } from '/@/constants/map'
import { getSystemConfig } from '/@/api/system/dict'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { ref } from 'vue'
import logoPng from '/@/assets/largeScreen1/logo@2x.png'
import axios from 'axios'
import { currentClientConfig } from '/@/config/env'
import { AMapConfig } from '/@/constants/index'
function getPinPosition (pinAMapPosition: pinAMapPosition):MapGeographicPosition {
  return { height: 0, latitude: pinAMapPosition.lat, longitude: pinAMapPosition.lng }
}
export function generatePointContent (pinAMapPosition: pinAMapPosition) {
  const position = getPinPosition(pinAMapPosition)
  return {
    type: MapElementEnum.PIN,
    content: generatePoint(position, {
      color: MapDoodleColor.PinColor,
      clampToGround: true,
    })
  }
}
function getLieOrPolyPosition (mapPosition: pinAMapPosition[]):MapGeographicPosition[] {
  const position = [] as MapGeographicPosition[]
  mapPosition.forEach(item => {
    position.push({ height: 0, latitude: item.lat, longitude: item.lng })
  })
  return position
}
export function generateLineContent (mapPosition: pinAMapPosition[]) {
  const position = getLieOrPolyPosition(mapPosition)
  return {
    type: MapElementEnum.LINE,
    content: generateLine(position, {
      color: MapDoodleColor.PolylineColor,
      directConnected: false,
    })
  }
}

export function generatePolyContent (mapPosition: pinAMapPosition[]) {
  const position = getLieOrPolyPosition(mapPosition)
  return {
    type: MapElementEnum.POLY,
    content: generatePolygon(position, {
      color: MapDoodleColor.PolygonColor,
    })
  }
}

export function generateCircleContent (centerPosition: pinAMapPosition, radius: number) {
  const position = getPinPosition(centerPosition)
  return {
    type: MapElementEnum.CIRCLE,
    content: {
      type: 'Feature',
      properties: {
        color: MapDoodleColor.CircleColor,
        clampToGround: true
      },
      geometry: {
        type: 'Circle',
        coordinates: [position.longitude, position.latitude],
        radius
      }
    }
  }
}

export interface LoadMapOptions {
  plugins?: string[]
  mapType?: 'satellite' | 'roadmap'
}

/**
 * 统一加载高德地图环境（自动处理在线/离线）
 * @param options 加载参数：插件列表 plugins，底图类型 mapType (默认 'satellite')
 * @returns { AMap: any, layers: any[] } 初始化好的地图对象与底图图层数组
 */
export async function loadMapEnvironment (options: LoadMapOptions = {}): Promise<{ AMap: any, layers: any[] }> {
  const { plugins = [], mapType = 'satellite' } = options
  const online = !offlineSites.value.includes(window.location.origin)

  if (online) {
    window._AMapSecurityConfig = {
      securityJsCode: CURRENT_CONFIG.securityJsCode,
    }
    const AMapLoader = await import('@amap/amap-jsapi-loader')
    const mergedPlugins = Array.from(new Set([...(AMapConfig.plugins || []), ...(plugins || [])]))
    const AMap = await AMapLoader.default.load({
      ...AMapConfig,
      plugins: mergedPlugins
    })

    let layers = []
    if (currentClientConfig.useCustomMap) {
      layers = getCustomBaseLayers(AMap)
    } else {
      layers = createTiandituLayers()
    }

    return { AMap, layers }
  } else {
    await loadOfflineScript('./amap/AMap3.js')
    const AMap = (window as any).AMap

    if (mapType === 'roadmap') {
      const roadmapLayer = new AMap.TileLayer({
        getTileUrl: (x:string, y:string, z:string) => getOfflineImg(x, y, z),
        zIndex: 10
      })
      return { AMap, layers: [roadmapLayer] }
    } else {
      const satelliteLayer = new AMap.TileLayer({
        getTileUrl: (x:string, y:string, z:string) => getOfflineWxImg(x, y, z),
        zIndex: 10
      })
      const labelLayer = new AMap.TileLayer({
        getTileUrl: (x:string, y:string, z:string) => getOfflineBzImg(x, y, z),
        zIndex: 11
      })
      return { AMap, layers: [satelliteLayer, labelLayer] }
    }
  }
}

/** 注入 Linux/UOS 系统兼容配置（在加载离线 AMap 前执行） */
function injectLinuxCompatConfig () {
  const win = window as any
  // 1. 强制启用 WebGL 渲染（核心：解决 Linux WebGL 检测误判）
  win.forceWebGL = true
  win.forceWebGLBaseRender = true
  // 2. 禁用高德的浏览器版本检测（避免 Linux UA 被判定为不兼容）
  win.AMapDisableBrowserVersionCheck = true
  // 3. 禁用 WebGL 性能检测（Linux 下 Mesa 驱动性能检测易触发降级）
  win.AMapDisableWebGLPerformanceCheck = true
  // 4. 统信 UOS 系统特殊配置
  if (navigator.userAgent.includes('UOS') || navigator.userAgent.includes('Linux')) {
    win.AMapForceCanvasFallback = false // 禁止强制 Canvas 降级
    win.AMapWebGLContextAttributes = {
      preserveDrawingBuffer: true,
      antialias: false, // 关闭抗锯齿，提升性能
      alpha: true
    }
    console.log('[AMap] 已注入 Linux/UOS 兼容配置')
  }
}

const scriptLoadCache: Record<string, Promise<unknown>> = {}

/** 动态加载本地 JS 文件（自动注入 Linux/UOS 兼容配置） */
export function loadOfflineScript (src:string) {
  // 在加载离线 AMap 脚本前注入兼容配置
  injectLinuxCompatConfig()

  if (scriptLoadCache[src]) {
    return scriptLoadCache[src]
  }

  scriptLoadCache[src] = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`)
    if (existingScript) {
      resolve(true)
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.onload = resolve
    script.onerror = reject
    document.head.appendChild(script)
  })

  return scriptLoadCache[src]
}

export const offlineSites = ref<string[]>([])
export const offlineWeatherSite = ref('')
export const offlineSysCon = ref(logoPng)
export const offlineSysNameFirst = ref('低空智巡')
export const offlineSysNameLast = ref('低空智巡平台')
export const offlineIsDeepSeek = ref(true)
// ================= 加载状态 =================
let loaded = false
let loadingPromise: Promise<void> | null = null
// ================= 初始化（只会请求一次） =================
export function loadOfflineConfig (): Promise<void> {
  if (loaded) {
    return Promise.resolve()
  }
  if (loadingPromise) {
    return loadingPromise
  }
  loadingPromise = getSystemConfig(1999)
    .then(res => {
      if (res.code === 0) {
        const data = res.data || []
        const offMapWeather = data.find(it => it.value === 'offMapWeather')?.alias
        const offMapName = data.find(it => it.value === 'offMapName')?.alias
        const sysCon = data.find(it => it.value === 'sysCon')?.alias
        const sysNameFirst = data.find(it => it.value === 'sysNameFirst')?.alias
        const sysNameLast = data.find(it => it.value === 'sysNameLast')?.alias
        const isDeepSeek = data.find(it => it.value === 'isDeepSeek')?.alias

        const dbAmapKey = data.find(it => it.value === 'amapKey')?.alias
        const dbSecurityJsCode = data.find(it => it.value === 'securityJsCode')?.alias
        const dbAppKey = data.find(it => it.value === 'appKey')?.alias
        const dbAppLicense = data.find(it => it.value === 'appLicense')?.alias
        const dbAppId = data.find(it => it.value === 'appId')?.alias
        const dbTiandituTk = data.find(it => it.value === 'tiandituTk')?.alias
        const dbCesiumToken = data.find(it => it.value === 'cesiumToken')?.alias

        if (dbAmapKey) {
          CURRENT_CONFIG.amapKey = dbAmapKey
          AMapConfig.key = dbAmapKey
        }
        if (dbSecurityJsCode) CURRENT_CONFIG.securityJsCode = dbSecurityJsCode
        if (dbAppKey) CURRENT_CONFIG.appKey = dbAppKey
        if (dbAppLicense) CURRENT_CONFIG.appLicense = dbAppLicense
        if (dbAppId) CURRENT_CONFIG.appId = dbAppId
        if (dbTiandituTk) CURRENT_CONFIG.tiandituKey = dbTiandituTk
        if (dbCesiumToken) CURRENT_CONFIG.cesiumToken = dbCesiumToken

        if (offMapName) offlineSites.value = offMapName.split(',')
        if (offMapWeather) offlineWeatherSite.value = offMapWeather
        if (sysCon) offlineSysCon.value = sysCon
        if (sysNameFirst) {
          offlineSysNameFirst.value = sysNameFirst
          document.title = sysNameFirst
        }
        if (sysNameLast) offlineSysNameLast.value = sysNameLast
        if (isDeepSeek) offlineIsDeepSeek.value = isDeepSeek === '1'
        loaded = true
      }
    })
    .catch(err => {
      loaded = false
      loadingPromise = null
      throw err
    })
  return loadingPromise
}
// ================= 状态判断 =================
export function isOfflineLoaded () {
  return loaded
}
export function getOfflineImg (x:string, y:string, z:string) {
  return `/kfk/roadmap/${z}/${x}/${y}.png`
}
/** 离线卫星瓦片 URL
 *  无参数: 返回模板字符串（Cesium UrlTemplateImageryProvider）
 *  有参数: 返回拼好的 URL（AMap getTileUrl 回调）
 */
export function getOfflineWxImg (): string
export function getOfflineWxImg (x: string, y: string, z: string): string
export function getOfflineWxImg (x?: string, y?: string, z?: string): string {
  if (x !== undefined && y !== undefined && z !== undefined) {
    return `/kfk/satellite/${z}/${x}/${y}.jpg`
  }
  return '/kfk/satellite/{z}/{x}/{y}.jpg'
}

export function getOfflineBzImg (x:string, y:string, z:string) {
  return `/kfk/overlay/${z}/${x}/${y}.png`
}
export function getOffline3dTerrainImg () {
  return '/kfk/terrain'
}

/**
 * 创建天地图 WGS-84 瓦片图层（卫星底图 + 矢量注记）
 * 用于替换高德默认 GCJ-02 底图
 */
export function createTiandituLayers () {
  const AMap = (window as any).AMap
  const tk = CURRENT_CONFIG.tiandituKey
  // 卫星底图（img_w = 球面墨卡托投影卫星图）
  const satLayer = new AMap.TileLayer({
    getTileUrl: (x: number, y: number, z: number) =>
      `https://t${Math.floor(Math.random() * 8)}.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX=${z}&TILEROW=${y}&TILECOL=${x}&tk=${tk}`,
    zIndex: 1
  })
  // 矢量注记（cva_w = 球面墨卡托投影矢量注记）
  const labelLayer = new AMap.TileLayer({
    getTileUrl: (x: number, y: number, z: number) =>
      `https://t${Math.floor(Math.random() * 8)}.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX=${z}&TILEROW=${y}&TILECOL=${x}&tk=${tk}`,
    zIndex: 2
  })
  return [satLayer, labelLayer]
}

export function createCesiumTiandituImageryLayers (Cesium: any) {
  const tk = CURRENT_CONFIG.tiandituKey
  const buildUrl = (layer: 'img' | 'cva') =>
    `https://t{s}.tianditu.gov.cn/${layer}_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer}&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`

  return [
    new Cesium.ImageryLayer(
      new Cesium.UrlTemplateImageryProvider({
        url: buildUrl('img'),
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        maximumLevel: 18,
        minimumLevel: 1
      })
    ),
    new Cesium.ImageryLayer(
      new Cesium.UrlTemplateImageryProvider({
        url: buildUrl('cva'),
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        maximumLevel: 18,
        minimumLevel: 1
      })
    )
  ]
}

// ================= 矢量图层分类数据源 =================
const STATIC_RESOURCE_BASE = '/staticResource/'

export const VECTOR_CATEGORIES = [
  { id: 'jzgd', label: '建筑工地', url: `${STATIC_RESOURCE_BASE}jzgd.json`, icon: 'icon-jianzhugongdi' },
  { id: 'jzbh', label: '建筑变化', url: `${STATIC_RESOURCE_BASE}jzbh.json`, icon: 'icon-build' },
  { id: 'gdfnfl', label: '耕地非农非粮', url: `${STATIC_RESOURCE_BASE}gdfnfl.json`, icon: 'icon-gengdi' },
  { id: 'yjzbhj', label: '沿江周边环境', url: `${STATIC_RESOURCE_BASE}yjzbhj.json`, icon: 'icon-a-Frame80' },
  { id: 'ljzt', label: '无序堆放垃圾渣土', url: `${STATIC_RESOURCE_BASE}ljzt.json`, icon: 'icon-tudui' }
]

// ================= 哈尔滨项目公共地图方法 =================
export function getCustomBaseLayers (AMap: any) {
  const TIAN_DI_TU_TK = '38dc2a7023232adef999f6aa8165a068'
  const tiandituSatLayer = new AMap.TileLayer({
    zIndex: 0,
    getTileUrl: (x: number, y: number, z: number) => `https://t0.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX=${z}&TILEROW=${y}&TILECOL=${x}&tk=${TIAN_DI_TU_TK}`
  })
  const customBaseLayer = new AMap.TileLayer({
    zIndex: 1,
    getTileUrl: (x: number, y: number, z: number) => `/staticResource/maps/${z}/${x}/${y}.webp`
  })
  const tiandituVectorLayer = new AMap.TileLayer({
    zIndex: 2,
    getTileUrl: (x: number, y: number, z: number) => `https://t0.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX=${z}&TILEROW=${y}&TILECOL=${x}&tk=${TIAN_DI_TU_TK}`
  })
  return [tiandituSatLayer, customBaseLayer, tiandituVectorLayer]
}

export function loadCustomVectorLayer (AMap: any, mapInstance: any, options?: { id?: string, url?: string, icon?: string, showFlyButton?: boolean, showDeleteButton?: boolean, getFlyDisabled?: () => boolean, onFlyTo?: (lng: number, lat: number) => void, onDelete?: (props: any, overlay: any) => void }) {
  const overlayGroup = new AMap.OverlayGroup()
  mapInstance.add(overlayGroup)

  const geojsonUrl = options?.url
  if (!geojsonUrl) return { clear: () => {} }
  axios.get(geojsonUrl + '?t=' + new Date().getTime()).then((res) => {
    const data = res.data
    const vectorInfoWindow = new AMap.InfoWindow({
      isCustom: true,
      offset: new AMap.Pixel(0, -35),
      autoMove: true
    })

    const buildPopupHtml = (props: any) => {
      let flyBtnHtml = ''
      if (options?.showFlyButton) {
        const flyLng = props.lon || props.zxdjd
        const flyLat = props.lat || props.zxdwd
        if (flyLng && flyLat) {
          const isDisabled = options.getFlyDisabled ? options.getFlyDisabled() : false
          const disabledAttr = isDisabled ? 'disabled="disabled"' : ''
          const disabledStyle = isDisabled ? 'opacity: 0.5; cursor: not-allowed;' : 'cursor: pointer;'
          flyBtnHtml = `
            <button onclick="window._vectorFlyTo()" ${disabledAttr} style="background-color: #2D8CF0; color: white; border: none; padding: 4px 12px; border-radius: 4px; font-size: 12px; display: flex; align-items: center; height: 26px; ${disabledStyle}">
              飞往此处
            </button>
          `
        }
      }
      let deleteBtnHtml = ''
      if (options?.showDeleteButton) {
        deleteBtnHtml = `
          <button onclick="window._vectorDelete()" style="background-color: #ff4d4f; color: white; border: none; padding: 4px 12px; border-radius: 4px; font-size: 12px; display: flex; align-items: center; height: 26px; cursor: pointer;">
            删除
          </button>
        `
      }
      return `
        <div style="position: relative; background-color: rgba(255, 255, 255, 0.95); backdrop-filter: blur(4px); box-shadow: 0 4px 16px rgba(0,0,0,0.2); border-radius: 6px; padding:12px; min-width:260px; font-size:13px; line-height:1.8; color:#333; border: 1px solid rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom:8px; padding-bottom:6px; border-bottom:2px solid #2D8CF0;">
            <div style="font-size:15px; font-weight:bold; max-width: 140px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              ${props.dlmc || '未知类型'}
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              ${deleteBtnHtml}
              ${flyBtnHtml}
              <div onclick="window._vectorClosePopup()" style="cursor: pointer; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; border-radius: 4px; background: rgba(0,0,0,0.05); color: #666; font-size: 16px; line-height: 1;" onmouseover="this.style.background='rgba(0,0,0,0.1)'; this.style.color='#333'" onmouseout="this.style.background='rgba(0,0,0,0.05)'; this.style.color='#666'">
                ×
              </div>
            </div>
          </div>
          <div><b>图斑编号：</b>${props.tbbh || '-'}</div>
          <div><b>图斑类型：</b>${props.type || '-'}</div>
          <div><b>图斑面积：</b>${props.tbmj ? props.tbmj + ' ㎡' : '-'}</div>
          <div><b>地类编码：</b>${props.dlbm || '-'}</div>
          <div><b>所属区域：</b>${(props.shiming || '') + (props.xianming || '') + (props.zhenming || '') + (props.shequ || '') || '-'}</div>
          <div><b>周边环境：</b>${props.zbhj || '-'}</div>
          <div><b>中心经纬：</b>${(props.lon || props.zxdjd) ? ((props.lon || props.zxdjd) + ', ' + (props.lat || props.zxdwd)) : '-'}</div>
          <div style="margin-top:6px; padding-top:6px; border-top:1px solid #eee; color:#666;">
            <b>详细地址：</b>${props.address || '-'}
          </div>
          ${props.bz ? '<div><b>备注：</b>' + props.bz + '</div>' : ''}
          <div style="color:#999; font-size:11px; margin-top:4px;">监测时间：${props.datatime || '-'}</div>
          
          <!-- 底部小三角 -->
          <div style="position: absolute; bottom: -8px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 8px solid rgba(255, 255, 255, 0.95);"></div>
        </div>
      `
    }

    const onPolygonClick = (props: any, e: any, targetPolygon: any) => {
      console.log('[矢量图层] 点击区块，全部属性：', props)

      const flyLng = props.lon || props.zxdjd
      const flyLat = props.lat || props.zxdwd
      if (flyLng && flyLat) {
        window._vectorFlyTo = () => {
          if (options?.getFlyDisabled && options.getFlyDisabled()) return
          if (options?.onFlyTo) options.onFlyTo(flyLng, flyLat)
        }
      }

      window._vectorClosePopup = () => {
        vectorInfoWindow.close()
      }

      window._vectorDelete = () => {
        if (options?.onDelete) options.onDelete(props, targetPolygon)
      }

      vectorInfoWindow.setContent(buildPopupHtml(props))
      vectorInfoWindow.open(mapInstance, e.lnglat)

      ;(window as any)._vectorPopupCtx = {
        props,
        infoWindow: vectorInfoWindow,
      }
    }

    vectorInfoWindow.on('close', () => {
      ;(window as any)._vectorPopupCtx = null
    })

    ;(window as any)._vectorRefreshPopup = () => {
      const ctx = (window as any)._vectorPopupCtx
      if (ctx && ctx.infoWindow && ctx.props) {
        ctx.infoWindow.setContent(buildPopupHtml(ctx.props))
      }
    }

    const addLabel = (props: any, coords: any) => {
      let centerLng = props.lon || props.zxdjd
      let centerLat = props.lat || props.zxdwd

      // Fallback if no center coordinate in properties
      if (!centerLng || !centerLat) {
        if (coords && coords[0] && coords[0][0]) {
          centerLng = coords[0][0][0]
          centerLat = coords[0][0][1]
        }
      }

      if (centerLng && centerLat) {
        if (options?.icon) {
          const marker = new AMap.Marker({
            position: new AMap.LngLat(centerLng, centerLat),
            content: `<div style="color: #FFD700; font-size: 24px; text-shadow: 1px 1px 2px rgba(0,0,0,0.8);"><i class="iconfont ${options.icon}" style="font-size: 24px;"></i></div>`,
            anchor: 'center', // 只有图标时，居中显示更好
            offset: new AMap.Pixel(0, 0),
            zIndex: 61,
            extData: props
          })
          marker.on('click', (e: any) => onPolygonClick(props, e, marker))
          overlayGroup.addOverlay(marker)
        }
      }
    }

    data.features.forEach((feature: any) => {
      const geomType = feature.geometry?.type
      const coords = feature.geometry?.coordinates

      if (geomType === 'Polygon') {
        const polygon = new AMap.Polygon({
          path: coords[0].map((c: any) => new AMap.LngLat(c[0], c[1])),
          fillOpacity: 0.35,
          fillColor: '#FFD700',
          strokeOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#FFD700',
          zIndex: 50,
          bubble: false,
          cursor: 'pointer',
          extData: feature.properties
        })
        polygon.on('click', (e: any) => onPolygonClick(feature.properties, e, polygon))
        overlayGroup.addOverlay(polygon)
        addLabel(feature.properties, coords)
      } else if (geomType === 'MultiPolygon') {
        coords.forEach((polygonCoords: any) => {
          const polygon = new AMap.Polygon({
            path: polygonCoords[0].map((c: any) => new AMap.LngLat(c[0], c[1])),
            fillOpacity: 0.35,
            fillColor: '#FFD700',
            strokeOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#FFD700',
            zIndex: 50,
            bubble: false,
            cursor: 'pointer',
            extData: feature.properties
          })
          polygon.on('click', (e: any) => onPolygonClick(feature.properties, e, polygon))
          overlayGroup.addOverlay(polygon)
        })
        addLabel(feature.properties, coords[0])
      }
    })
  }).catch(err => {
    console.error('[矢量图层] 加载失败:', err)
  })

  return {
    clear: () => {
      overlayGroup.clearOverlays()
      mapInstance.remove(overlayGroup)
      mapInstance.clearInfoWindow()
    },
    removeByTbbh: (tbbh: string) => {
      // 复制一份数组防止遍历时因 removeOverlay 改变原数组长度导致漏删
      const overlays = [...overlayGroup.getOverlays()]
      overlays.forEach((o: any) => {
        if (typeof o.getExtData === 'function') {
          const extData = o.getExtData()
          if (extData && extData.tbbh === tbbh) {
            overlayGroup.removeOverlay(o)
          }
        }
      })
    }
  }
}
